import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { Vessel } from './useData';
import { VesselStampPanel } from './StampPanel';

export const moduleConfig = {
  module: {
    title: 'Vessels',
    permissionModuleKey: 'vessels',
    route: {
      list: '/parameters/vessels',
      new: '/parameters/vessels/new',
      detail: (id: number) => `/parameters/vessels/${id}`,
      edit: (id: number) => `/parameters/vessels/${id}/edit`},
    api: {
      list: '/vessels',
      detail: (id: number) => `/vessels/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by vessel name, abbreviation, IMO or call sign...' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vesselName', 'isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'vesselName',
        label: 'Vessel name',
        placeholder: 'All vessel names',
        getValue: (item: Vessel) => item.vesselName,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: Vessel) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: Vessel[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.vesselName?.length) {
          const v = item.vesselName || '';
          if (!filters.vesselName.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New vessel',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/vessels/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: Vessel) => item.id,
  },
  emptyStates: {
    noData: 'Create a vessel to get started.',
    noSearchResults: 'No vessels match your search.',
    noFilterResults: 'No vessels match the filters.',
  },
  delete: {
    title: 'Delete vessel',
    message: 'Delete this vessel?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete vessel.',
    handler: async (id: number) => {
      const { deleteVessel } = await import('../../../services/mutations');
      await deleteVessel(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This vessel does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Vessel',
    getTitle: (item: Vessel) => item.vesselName || `Vessel #${item.id}`},
  form: {
    new: { title: 'New vessel', subtitle: 'Create a vessel record', submitText: 'Create' },
    edit: { title: 'Edit vessel', subtitle: 'Fleet register details', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<Vessel>) => {
      const { createVessel } = await import('../../../services/mutations');
      return createVessel(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<Vessel>) => {
      const { updateVessel } = await import('../../../services/mutations');
      return updateVessel(id, data as Record<string, unknown>);
    }},
  fields: [
    { name: 'vesselName', label: 'Vessel name', type: 'text', required: true, placeholder: 'e.g. Atlantic Trader', validation: { required: true, minLength: 1, maxLength: 100 } },
    { name: 'vesselAbrv', label: 'Abbreviation', type: 'text', placeholder: 'Short code', validation: { maxLength: 5 } },
    { name: 'imoNumber', label: 'IMO number', type: 'text', placeholder: 'IMO number', validation: { maxLength: 50 } },
    { name: 'callSign', label: 'Call sign', type: 'text', placeholder: 'Call sign', validation: { maxLength: 50 } },
    { name: 'officialNo', label: 'Official number', type: 'text', placeholder: 'Official number', validation: { maxLength: 50 } },
    { name: 'vesselTypeId', label: 'Vessel type', type: 'select', placeholder: 'Select vessel type', apiEndpoint: '/crew-lookups/vessel-types', valueField: 'id', labelField: 'name', clearable: true,
      mapApiItemToSelectOption: (item: { id: number; code?: string | null; name?: string }) => ({
        value: item.id,
        label: item.code ? `${item.code} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    { name: 'flagId', label: 'Flag', type: 'select', placeholder: 'Select flag country', apiEndpoint: '/countries', valueField: 'id', labelField: 'name', clearable: true,
      mapApiItemToSelectOption: (item: { id: number; name?: string; isoCode2?: string | null }) => ({
        value: item.id,
        label: item.isoCode2 ? `${item.isoCode2} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    { name: 'ownerId', label: 'Owner', type: 'select', placeholder: 'Select owner', apiEndpoint: '/crew-lookups/vessel-owners', valueField: 'id', labelField: 'name', clearable: true,
      mapApiItemToSelectOption: (item: { id: number; code?: string | null; name?: string }) => ({
        value: item.id,
        label: item.code ? `${item.code} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    {
      name: 'managerId',
      label: 'Management company',
      type: 'select',
      placeholder: 'Select management company',
      apiEndpoint: '/management-companies',
      valueField: 'id',
      labelField: 'name',
      clearable: true,
      mapApiItemToSelectOption: (item: { id: number; code?: string; name?: string }) => ({
        value: item.id,
        label: item.code ? `${item.code} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    { name: 'engId', label: 'Engine type', type: 'select', placeholder: 'Select engine type', apiEndpoint: '/crew-lookups/engine-types', valueField: 'id', labelField: 'name', clearable: true,
      mapApiItemToSelectOption: (item: { id: number; code?: string | null; name?: string }) => ({
        value: item.id,
        label: item.code ? `${item.code} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    { name: 'mainEngType', label: 'Main engine', type: 'text', placeholder: 'Make / model', validation: { maxLength: 50 } },
    { name: 'bhp', label: 'BHP', type: 'text', placeholder: 'Brake horsepower', validation: { maxLength: 50 } },
    { name: 'auxEng', label: 'Auxiliary engines', type: 'text', placeholder: 'Auxiliary engines', validation: { maxLength: 50 } },
    { name: 'craneTypes', label: 'Cranes', type: 'text', placeholder: 'Crane types', validation: { maxLength: 50 } },
    { name: 'ecdisType', label: 'ECDIS type', type: 'select', placeholder: 'Select ECDIS type', apiEndpoint: '/crew-lookups/ecdis-types', valueField: 'id', labelField: 'name', clearable: true,
      mapApiItemToSelectOption: (item: { id: number; code?: string | null; name?: string }) => ({
        value: item.id,
        label: item.code ? `${item.code} — ${item.name ?? ''}` : String(item.name ?? item.id),
      }),
      options: [{ value: '', label: 'None' }],
    },
    { name: 'ecdisModel', label: 'ECDIS model', type: 'text', placeholder: 'ECDIS model', validation: { maxLength: 50 } },
    { name: 'grt', label: 'GRT', type: 'number', placeholder: 'Gross tonnage' },
    { name: 'nrt', label: 'NRT', type: 'number', placeholder: 'Net register tonnage' },
    { name: 'dwt', label: 'DWT', type: 'number', placeholder: 'Deadweight tonnage' },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'vessel@example.com', validation: { maxLength: 100 } },
    { name: 'telex', label: 'Telex', type: 'text', placeholder: 'Telex', validation: { maxLength: 100 } },
    { name: 'pnumber', label: 'Phones', type: 'text', placeholder: 'Phone numbers', validation: { maxLength: 100 } },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Optional notes' },
    { name: 'safeManning', label: 'Safe manning', type: 'number', placeholder: 'e.g. 18', helperText: 'Minimum crew required by the Safe Manning Document' },
    { name: 'safeEquip', label: 'Safe equipment', type: 'switch', switchLabel: 'Safe equipment certificate', description: 'Vessel holds a valid safe equipment certificate', labelPosition: 'left', defaultValue: false },
    { name: 'isActive', label: 'Active', type: 'switch', switchLabel: 'Active in fleet', description: 'Shown in lists and available for operations', labelPosition: 'left', defaultValue: true },
    { name: 'isVessel', label: 'Is vessel', type: 'switch', switchLabel: 'Ship / vessel unit', description: 'Uncheck for offices or non-vessel fleet units', labelPosition: 'left', defaultValue: true },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identity',
        title: 'Identity',
        description: 'Name, registry identifiers, and vessel class.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselName', 'vesselAbrv', 'imoNumber', 'callSign', 'officialNo', 'vesselTypeId'],
        fieldSpans: {
          vesselName: 'md:col-span-8',
          vesselAbrv: 'md:col-span-4',
          imoNumber: 'md:col-span-4',
          callSign: 'md:col-span-4',
          officialNo: 'md:col-span-4',
          vesselTypeId: 'md:col-span-6',
        },
        collapsible: false,
      },
      {
        id: 'parties',
        title: 'Flag & parties',
        description: 'Flag state, owner, and ship manager.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['flagId', 'ownerId', 'managerId'],
        fieldSpans: {
          flagId: 'md:col-span-4',
          ownerId: 'md:col-span-4',
          managerId: 'md:col-span-4',
        },
        collapsible: false,
      },
      {
        id: 'machinery',
        title: 'Machinery & ECDIS',
        description: 'Main machinery details and electronic chart system.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['engId', 'mainEngType', 'bhp', 'auxEng', 'craneTypes', 'ecdisType', 'ecdisModel'],
        fieldSpans: {
          engId: 'md:col-span-4',
          mainEngType: 'md:col-span-5',
          bhp: 'md:col-span-3',
          auxEng: 'md:col-span-6',
          craneTypes: 'md:col-span-6',
          ecdisType: 'md:col-span-6',
          ecdisModel: 'md:col-span-6',
        },
        collapsible: false,
      },
      {
        id: 'tonnage',
        title: 'Tonnage',
        description: 'Registered and deadweight tonnage.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['grt', 'nrt', 'dwt'],
        fieldSpans: {
          grt: 'md:col-span-4',
          nrt: 'md:col-span-4',
          dwt: 'md:col-span-4',
        },
        collapsible: false,
      },
      {
        id: 'contact',
        title: 'Contact & notes',
        description: 'Ship contact details and free-form notes.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['email', 'telex', 'pnumber', 'notes'],
        fieldSpans: {
          email: 'md:col-span-6',
          telex: 'md:col-span-3',
          pnumber: 'md:col-span-3',
          notes: 'md:col-span-12',
        },
        collapsible: false,
      },
      {
        id: 'compliance',
        title: 'Compliance',
        description: 'Safe manning document and equipment certificate.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['safeManning', 'safeEquip'],
        fieldSpans: {
          safeManning: 'md:col-span-4',
          safeEquip: 'md:col-span-8',
        },
        collapsible: false,
      },
      {
        id: 'status',
        title: 'Status',
        description: 'Whether this unit is active and treated as a vessel.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive', 'isVessel'],
        fieldSpans: {
          isActive: 'md:col-span-6',
          isVessel: 'md:col-span-6',
        },
        collapsible: false,
      },
    ],
  } as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Vessel>[] => [
  { key: 'vesselName', header: 'Vessel Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'vesselAbrv', header: 'Abbreviation', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'imoNumber', header: 'IMO', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'callSign', header: 'Call Sign', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'vesselTypeId', header: 'Type', sortable: true, render: (_v, row) => (
    <TextCell
      value={
        row?.vesselType
          ? row.vesselType.code
            ? `${row.vesselType.code} — ${row.vesselType.name}`
            : row.vesselType.name
          : row?.vesselTypeId != null
            ? String(row.vesselTypeId)
            : undefined
      }
    />
  ) },
  { key: 'flagId', header: 'Flag', sortable: true, render: (_v, row) => (
    <TextCell
      value={
        row?.flag
          ? row.flag.isoCode2
            ? `${row.flag.isoCode2} — ${row.flag.name}`
            : row.flag.name
          : row?.flagId != null
            ? String(row.flagId)
            : undefined
      }
    />
  ) },
  { key: 'ownerId', header: 'Owner', sortable: true, render: (_v, row) => (
    <TextCell
      value={
        row?.owner
          ? row.owner.code
            ? `${row.owner.code} — ${row.owner.name}`
            : row.owner.name
          : row?.ownerId != null
            ? String(row.ownerId)
            : undefined
      }
    />
  ) },
  {
    key: 'manager',
    header: 'Management company',
    sortable: false,
    render: (_v, row) => (
      <TextCell
        value={
          row?.manager
            ? row.manager.code
              ? `${row.manager.code} — ${row.manager.name}`
              : row.manager.name
            : row?.managerId != null
              ? String(row.managerId)
              : undefined
        }
      />
    ),
  },
  { key: 'engId', header: 'Engine', sortable: true, render: (_v, row) => (
    <TextCell
      value={
        row?.engineType
          ? row.engineType.code
            ? `${row.engineType.code} — ${row.engineType.name}`
            : row.engineType.name
          : row?.engId != null
            ? String(row.engId)
            : undefined
      }
    />
  ) },
  { key: 'mainEngType', header: 'Main engine', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'bhp', header: 'BHP', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'auxEng', header: 'Aux engine', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'craneTypes', header: 'Cranes', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'ecdisType', header: 'ECDIS type', sortable: true, render: (_v, row) => (
    <TextCell
      value={
        row?.ecdisTypeRef
          ? row.ecdisTypeRef.code
            ? `${row.ecdisTypeRef.code} — ${row.ecdisTypeRef.name}`
            : row.ecdisTypeRef.name
          : row?.ecdisType != null
            ? String(row.ecdisType)
            : undefined
      }
    />
  ) },
  { key: 'ecdisModel', header: 'ECDIS model', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'officialNo', header: 'Official no.', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'grt', header: 'GRT', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'nrt', header: 'NRT', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'dwt', header: 'DWT', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'pnumber', header: 'Phones', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'telex', header: 'Telex', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'email', header: 'Email', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'notes', header: 'Notes', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'safeEquip', header: 'Safe equip', sortable: true, render: (v) => <TextCell value={v ? 'Yes' : 'No'} /> },
  { key: 'safeManning', header: 'Safe manning', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'isVessel', header: 'Is vessel', sortable: true, render: (v) => <TextCell value={v ? 'Yes' : 'No'} /> },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
  { key: 'updatedAt', header: 'Updated', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (
  item: Vessel,
  formatDate: (s?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  mutate?: () => void,
) => [
  {
    title: 'Stamp / seal',
    content: (
      <VesselStampPanel
        vesselId={item.id}
        stampStorageKey={item.stampStorageKey}
        stampOriginalFilename={item.stampOriginalFilename}
        onChanged={() => mutate?.()}
        editable
      />
    ),
  },
  {
    title: 'Identity',
    fields: [
      { label: 'Vessel name', value: item.vesselName },
      { label: 'Abbreviation', value: item.vesselAbrv || '' },
      { label: 'IMO number', value: item.imoNumber || '' },
      { label: 'Call sign', value: item.callSign || '' },
      { label: 'Official number', value: item.officialNo || '' },
      {
        label: 'Vessel type',
        value: item.vesselType
          ? item.vesselType.code
            ? `${item.vesselType.code} — ${item.vesselType.name}`
            : item.vesselType.name
          : '—',
      },
    ],
  },
  {
    title: 'Flag & parties',
    fields: [
      {
        label: 'Flag',
        value: item.flag
          ? item.flag.isoCode2
            ? `${item.flag.isoCode2} — ${item.flag.name}`
            : item.flag.name
          : '—',
      },
      {
        label: 'Owner',
        value: item.owner
          ? item.owner.code
            ? `${item.owner.code} — ${item.owner.name}`
            : item.owner.name
          : '—',
      },
      {
        label: 'Management company',
        value: item.manager
          ? item.manager.code
            ? `${item.manager.code} — ${item.manager.name}`
            : item.manager.name
          : '—',
      },
    ],
  },
  {
    title: 'Machinery & ECDIS',
    fields: [
      {
        label: 'Engine type',
        value: item.engineType
          ? item.engineType.code
            ? `${item.engineType.code} — ${item.engineType.name}`
            : item.engineType.name
          : '—',
      },
      { label: 'Main engine', value: item.mainEngType || '—' },
      { label: 'BHP', value: item.bhp || '—' },
      { label: 'Auxiliary engines', value: item.auxEng || '—' },
      { label: 'Cranes', value: item.craneTypes || '—' },
      {
        label: 'ECDIS type',
        value: item.ecdisTypeRef
          ? item.ecdisTypeRef.code
            ? `${item.ecdisTypeRef.code} — ${item.ecdisTypeRef.name}`
            : item.ecdisTypeRef.name
          : '—',
      },
      { label: 'ECDIS model', value: item.ecdisModel || '—' },
    ],
  },
  {
    title: 'Tonnage & contact',
    fields: [
      { label: 'GRT', value: item.grt != null ? String(item.grt) : '—' },
      { label: 'NRT', value: item.nrt != null ? String(item.nrt) : '—' },
      { label: 'DWT', value: item.dwt != null ? String(item.dwt) : '—' },
      { label: 'Email', value: item.email || '—' },
      { label: 'Telex', value: item.telex || '—' },
      { label: 'Phones', value: item.pnumber || '—' },
      { label: 'Notes', value: item.notes || '—' },
    ],
  },
  {
    title: 'Compliance',
    fields: [
      { label: 'Safe manning', value: item.safeManning != null ? String(item.safeManning) : '—' },
      { label: 'Safe equipment', value: item.safeEquip ? 'Yes' : 'No' },
    ],
  },
  {
    title: 'Status',
    fields: [
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Is vessel', value: item.isVessel ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ],
  },
  {
    title: 'Supply',
    fields: [
      {
        label: 'Supply Requisition',
        value: (
          <a
            href={`${(process.env.REACT_APP_VESSEL_APP_URL || 'http://localhost:3001').replace(/\/$/, '')}/requisitions`}
            className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open vessel portal requisitions
          </a>
        )},
    ]},
];

const TableActionsCell: React.FC<{
  item: Vessel;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: Vessel,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<Vessel> => ({
  vesselName: '',
  vesselAbrv: '',
  imoNumber: '',
  callSign: '',
  email: '',
  notes: '',
  safeEquip: false,
  isActive: true,
  isVessel: true});

export const mapItemToFormData = (item: Vessel): Partial<Vessel> => ({
  vesselName: item.vesselName,
  vesselAbrv: item.vesselAbrv ?? '',
  vesselTypeId: (item.vesselTypeId ? String(item.vesselTypeId) : '') as unknown as number,
  vesselType: item.vesselType,
  imoNumber: item.imoNumber ?? '',
  callSign: item.callSign ?? '',
  flagId: (item.flagId ? String(item.flagId) : '') as unknown as number,
  flag: item.flag,
  ownerId: (item.ownerId ? String(item.ownerId) : '') as unknown as number,
  owner: item.owner,
  managerId: (item.managerId ? String(item.managerId) : '') as unknown as number,
  manager: item.manager,
  engId: (item.engId ? String(item.engId) : '') as unknown as number,
  engineType: item.engineType,
  mainEngType: item.mainEngType ?? '',
  bhp: item.bhp ?? '',
  auxEng: item.auxEng ?? '',
  craneTypes: item.craneTypes ?? '',
  ecdisType: (item.ecdisType ? String(item.ecdisType) : '') as unknown as number,
  ecdisTypeRef: item.ecdisTypeRef,
  ecdisModel: item.ecdisModel ?? '',
  officialNo: item.officialNo ?? '',
  grt: item.grt ?? undefined,
  nrt: item.nrt ?? undefined,
  dwt: item.dwt ?? undefined,
  pnumber: item.pnumber ?? '',
  telex: item.telex ?? '',
  email: item.email ?? '',
  notes: item.notes ?? '',
  safeEquip: item.safeEquip ?? false,
  safeManning: item.safeManning ?? undefined,
  isActive: item.isActive ?? true,
  isVessel: item.isVessel ?? true});

export type ModuleConfig = typeof moduleConfig;
